/**
 * The coach's bookkeeping (ROUND7 §5), with no DOM in it so a test can drive it tick by tick: every step in
 * course order, which are done, and which one to say now. A step is ticked the moment the world shows it done,
 * whether or not it was the one on show - doing a later step early is fine - and one that is not worth saying
 * (not yet possible, or its station is behind him) is passed over rather than waited on.
 */

import type { SimEvent, World } from '../../sim/types';
import { type Facts, newFacts, observe } from './facts';
import type { KeyNames } from './keys';
import { AIR_STEPS } from './stepsAir';
import { DOG_STEPS } from './stepsDog';
import { FIELD_STEPS, SEEN_STEPS } from './stepsField';
import { GROUND_STEPS } from './stepsGround';
import type { Step, StepId, StepView } from './stepTypes';

/** Every lesson on the course, south to north. */
export const STEPS: readonly Step[] = [...AIR_STEPS, ...GROUND_STEPS, ...SEEN_STEPS, ...DOG_STEPS, ...FIELD_STEPS];

/** What the panel shows: the step, where it is in the course, and its words. */
export interface CoachLine {
  id: StepId;
  station: string;
  /** 1-based position in `STEPS`, and how many there are. */
  index: number;
  count: number;
  text: string;
  note: string;
}

export class CoachProgress {
  readonly facts: Facts = newFacts();
  private readonly done = new Set<StepId>();
  /** Steps ticked on the latest observed tick, in course order. */
  private fresh: StepId[] = [];
  private world: World | null = null;

  constructor(private readonly keys: KeyNames) {}

  /** Feed one sim step: its events, then the world after it. */
  observe(world: World, events: readonly SimEvent[]): void {
    observe(this.facts, world, events);
    this.world = world;
    const view = this.view(world);
    this.fresh = [];
    for (const step of STEPS) {
      if (this.done.has(step.id) || !step.done(view)) continue;
      this.done.add(step.id);
      this.fresh.push(step.id);
    }
  }

  isDone(id: StepId): boolean {
    return this.done.has(id);
  }

  /** Every step's id that is done, in course order. */
  doneIds(): StepId[] {
    return STEPS.filter((s) => this.done.has(s.id)).map((s) => s.id);
  }

  allDone(): boolean {
    return this.done.size === STEPS.length;
  }

  /** What was ticked on the latest tick (for the panel's "done" flash). */
  justDone(): readonly StepId[] {
    return this.fresh;
  }

  /** The step to say now, or null when there is nothing to say. */
  current(): Step | null {
    const world = this.world;
    if (world === null) return null;
    const view = this.view(world);
    // The aircraft over the X waits for nobody: nothing else is said while it hovers.
    const ladder = STEPS.find((s) => s.id === 'ladder');
    if (world.osprey.phase === 'hovering' && ladder && !this.done.has('ladder')) return ladder;
    return STEPS.find((s) => !this.done.has(s.id) && s.ready(view)) ?? null;
  }

  /** The current step in words, or null. */
  line(): CoachLine | null {
    const step = this.current();
    const world = this.world;
    if (step === null || world === null) return null;
    const view = this.view(world);
    return {
      id: step.id,
      station: step.station,
      index: STEPS.indexOf(step) + 1,
      count: STEPS.length,
      text: step.text(view),
      note: step.note(view),
    };
  }

  titleOf(id: StepId): string {
    return STEPS.find((s) => s.id === id)?.title ?? id.toUpperCase();
  }

  private view(world: World): StepView {
    return { world, facts: this.facts, keys: this.keys };
  }
}
